"use client";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Phone, Mail, MapPin, Clock, Instagram, Facebook, Youtube } from "lucide-react";
import HeaderPage from "@/components/header-page";
import { useTranslations } from 'next-intl';

export default function ContactClient() {
  const t = useTranslations();
  const [formData, setFormData] = useState({
    firstName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        alert(t('forms.messageSent'));
        setFormData({ firstName: "", email: "", phone: "", subject: "", message: "" });
      } else {
        alert(t('forms.messageError'));
      }
    } catch (error) {
      alert(t('forms.messageError'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div>
      <HeaderPage
        title={t('navigation.contact')}
        breadcrumbs={[
          { label: t('common.home'), href: "/" },
          { label: t('navigation.contact') }
        ]}
      />
      <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-8 animate-fade-in">
            {t('contact.formDescription')}
          </h2>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="animate-fade-in-up delay-100">
                    <label className="block text-sm font-medium text-contact-label mb-2">
                      {t('contact.firstName')}
                    </label>
                    <Input
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="contact-input"
                      placeholder=""
                    />
                  </div>
                  <div className="animate-fade-in-up delay-200">
                    <label className="block text-sm font-medium text-contact-label mb-2">
                      {t('forms.email')}
                    </label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="contact-input"
                      placeholder=""
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="animate-fade-in-up delay-300">
                    <label className="block text-sm font-medium text-contact-label mb-2">
                      {t('forms.phone')}
                    </label>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="contact-input"
                      placeholder=""
                    />
                  </div>
                  <div className="animate-fade-in-up delay-400">
                    <label className="block text-sm font-medium text-contact-label mb-2">
                      {t('forms.subject')}
                    </label>
                    <Input
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="contact-input"
                      placeholder=""
                    />
                  </div>
                </div>

                <div className="animate-fade-in-up delay-500">
                  <label className="block text-sm font-medium text-contact-label mb-2">
                    {t('forms.message')}
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="contact-input min-h-[180px] resize-none"
                    placeholder=""
                  />
                </div>

                <div className="flex justify-end animate-fade-in-up delay-600">
                  <Button type="submit" disabled={loading} className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-2 rounded-full">
                    {loading ? t('common.loading') : t('contact.send')}
                  </Button>
                </div>
              </form>
            </div>

            <div className="animate-slide-in-right">
              <div className="bg-contact-card rounded-2xl p-8 text-contact-card-foreground h-full relative overflow-hidden">
                <div className="absolute bottom-4 right-4 w-32 h-32 rounded-full bg-white/5"></div>
                <div className="absolute bottom-12 right-12 w-24 h-24 rounded-full bg-white/5"></div>
                
                <h2 className="text-xl font-semibold mb-8">{t('contact.ourCoordinates')}</h2>

                <div className="space-y-6 relative z-10">
                  <div className="flex items-start gap-4">
                    <Phone className="w-5 h-5 mt-1 flex-shrink-0" />
                    <span>+216 71 168 600 -71 168 604</span>
                  </div>

                  <div className="flex items-start gap-4">
                    <Mail className="w-5 h-5 mt-1 flex-shrink-0" />
                    <span>contact@tunisiagotravel.com</span>
                  </div>

                  <div className="flex items-start gap-4">
                    <MapPin className="w-5 h-5 mt-1 flex-shrink-0" />
                    <span>8 Rue Bachaya Bent Maaouia El Manar 1, Tunisie</span>
                  </div>

                  <div className="flex items-start gap-4">
                    <Clock className="w-5 h-5 mt-1 flex-shrink-0" />
                    <div>
                      <p>{t('contact.openingHours')}:</p>
                      <p>{t('contact.weekdays')}: 09:00 - 20:00</p>
                      <p>{t('contact.weekends')}: 10:30 - 22:00</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 mt-8 relative z-10">
                  <a href="#" className="hover:opacity-80 transition-opacity">
                    <Instagram className="w-6 h-6" />
                  </a>
                  <a href="#" className="hover:opacity-80 transition-opacity">
                    <Facebook className="w-6 h-6" />
                  </a>
                  <a href="#" className="hover:opacity-80 transition-opacity">
                    <Youtube className="w-6 h-6" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
